import {
  Typography,
  MatchUp,
  StatsTeams,
  StatsAtBatPromotion,
  StatsVenue,
  StatsGameStatus,
  createCssVariableString,
  useBreakpoints,
} from "best-common-react";
import React from "react";
import styled from "styled-components";
import RouteConstants, { urlReplace } from "../../../constants/RouteConstants";
import { EventTimingDTO, EventTimingTypeDTO } from "../../../types/EventTiming";
import { GameRequestCountDTO } from "../../../types/Game";
import { useTimezone } from "../../../util/TimeUtil";
import Promotions from "./Promotions";
import TicketRequestSection from "./TicketRequestSection";
import { useLocation, useNavigate } from "react-router";
import clsx from "clsx";

type GameCardProps = {
  $isPromotion: boolean;
};

const GameCard = styled.div<GameCardProps>`
  width: 100%;
  color: ${createCssVariableString("onColorsOnSurfaceLow")};
  border: 1px solid ${createCssVariableString("outlineColorsOutlineVariant")};
  ${(props) => props.$isPromotion && "border-bottom: 0;"}
  background-color: ${createCssVariableString("surfaceColorsSurfaceContainerLowest")};
  margin: 0.5rem 0 0 0;
  padding: 0.75rem 1rem;
  box-shadow: ${createCssVariableString("bcr-shadows")};
`;

type GameProps = {
  gamePk: number;
  gameDate: string;
  venue: StatsVenue;
  description: string;
  teams: StatsTeams;
  access: EventTimingTypeDTO;
  cutoff: EventTimingDTO;
  personalRequests: boolean;
  blackedOut: boolean;
  requestCount: GameRequestCountDTO;
  status: StatsGameStatus;
  promotions?: StatsAtBatPromotion[];
};

const Game = ({
  gamePk,
  gameDate,
  venue,
  teams,
  access,
  cutoff,
  promotions = [],
  personalRequests,
  requestCount,
  description,
  blackedOut,
  status,
}: GameProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoints();
  const { time, day, date } = useTimezone(gameDate, venue.timeZone, status);

  const toRequest = (type: string) => {
    navigate(urlReplace(RouteConstants.REQUESTS.TICKETS, { gamePk, type }), {
      state: { previousLocation: location },
    });
  };

  return (
    <div>
      <GameCard $isPromotion={!!promotions}>
        <div className="d-flex justify-content-between flex-wrap">
          <div className={clsx("d-flex align-items-start bcr-gap-4", { "w-100 justify-content-between": isMobile })}>
            <div className="d-flex bcr-gap-4 align-items-start">
              <div>
                <Typography variant="titleMedium" elementStyles={{ weight: "bold" }}>
                  {date}
                </Typography>
                {!isMobile && <Typography>{day}</Typography>}
              </div>
              {!isMobile && <MatchUp home={teams.home.team} away={teams.away.team} description={description} />}
              <Typography>{time}</Typography>
            </div>

            {!isMobile && (
              <div>
                <Typography>{venue.name}</Typography>
                {venue.location ? (
                  <Typography>
                    {venue.location.city}
                    {venue.location.city && venue.location.stateAbbrev ? ", " : ""}
                    {venue.location.stateAbbrev}
                  </Typography>
                ) : null}
              </div>
            )}
            {isMobile && (
              <div className="justify-self-end">
                <MatchUp home={teams.home.team} away={teams.away.team} description={description} />
              </div>
            )}
          </div>
          <TicketRequestSection
            gameDate={gameDate}
            access={access}
            cutoff={cutoff}
            toRequest={toRequest}
            personalAllowed={personalRequests}
            requestCount={requestCount}
            gamePk={gamePk}
            blackedOut={blackedOut}
          />
        </div>
      </GameCard>
      <Promotions gamePk={gamePk} promotions={promotions} />
    </div>
  );
};

export default Game;
